import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getToken, STREAM_API } from '../../utils/streamApi';
import IndustrialLayout, { toast } from './IndustrialLayout';

// Best-effort "is this camera up" from whatever status fields the API returns.
function isOnline(cam) {
  if (cam.online !== undefined) return !!cam.online;
  if (cam.status) return /online|ok|up|healthy|active/i.test(String(cam.status));
  if (cam.enabled !== undefined) return !!cam.enabled;
  return true; // assume up if the API says nothing
}

// Download the camera's CURRENT frame as a JPEG. Uses the single-frame endpoint
// (JWT via ?token=), so it grabs a real snapshot without touching the MJPEG stream.
async function downloadSnapshot(idx, name, token) {
  try {
    const t = token || await getToken();
    const res = await fetch(`${STREAM_API}/cameras/${idx}/frame?token=${t}`);
    if (!res.ok) { toast('Snapshot failed — camera may be offline.', 'err'); return; }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    // Date.now via a fresh Date is fine here (browser runtime, not a workflow).
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    a.href = url;
    a.download = `${String(name || 'camera').replace(/[^\w-]+/g, '_')}_${stamp}.jpg`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast('Snapshot downloaded.', 'ok');
  } catch {
    toast('Snapshot failed.', 'err');
  }
}

// Lightweight live tile — the detection-fed MJPEG stream, always available when
// the camera is up (no extra RTSP connection). Falls back to a placeholder on error.
function LiveTile({ idx, token }) {
  const [failed, setFailed] = useState(false);
  if (!token || failed) return <div className="ph">📹</div>;
  return (
    <img src={`${STREAM_API}/cameras/${idx}/stream?token=${token}`} alt=""
      onError={() => setFailed(true)} />
  );
}

// Full-screen maximized view of one camera — live stream + snapshot-download.
function MaxView({ cam, token, onClose }) {
  const idx = cam.idx;
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,.9)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', maxWidth: 1280, marginBottom: 10 }}
        onClick={e => e.stopPropagation()}>
        <span style={{ color: '#fff', fontWeight: 700, letterSpacing: '.04em' }}>{(cam.name || `Camera ${idx}`).toUpperCase()}</span>
        <button className="ind-pill" style={{ marginLeft: 'auto' }}
          onClick={() => downloadSnapshot(idx, cam.name, token)}>📷 Snapshot</button>
        <button className="ind-pill" onClick={onClose}>✕ Close</button>
      </div>
      <div onClick={e => e.stopPropagation()}
        style={{ maxWidth: 1280, width: '100%', aspectRatio: '16 / 9', background: '#000', borderRadius: 8, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {token
          ? <img src={`${STREAM_API}/cameras/${idx}/stream?token=${token}`} alt=""
              style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          : <div className="ph">📹</div>}
      </div>
    </div>
  );
}

export default function IndustrialWallPage() {
  const navigate = useNavigate();
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [token, setToken]     = useState(null);
  const [live, setLive]       = useState(true);
  const [maxCam, setMaxCam]   = useState(null);   // camera shown maximized, or null

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [d, t] = await Promise.all([
          apiFetch('/ui/cameras').then(r => r.json()),
          getToken(),
        ]);
        if (!alive) return;
        setCameras(d.cameras || []);
        setToken(t);
      } catch {
        if (alive) setError('Failed to load cameras');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const { online, offline } = useMemo(() => {
    let on = 0, off = 0;
    cameras.forEach(c => (isOnline(c) ? on++ : off++));
    return { online: on, offline: off };
  }, [cameras]);

  return (
    <IndustrialLayout
      eyebrow="Camera wall"
      title="Live"
      subtitle={loading ? 'LOADING…' : error ? error : `${online} ONLINE · ${offline} DOWN · ${cameras.length} TOTAL`}
      right={
        <>
          <button className="ind-btn" onClick={() => navigate('/cameras')} title="Manage cameras">
            📹 Cameras
          </button>
          <button className="ind-btn" onClick={() => setLive(v => !v)}>
            {live ? '⏸ Pause live' : '▶ Go live'}
          </button>
        </>
      }>
      {loading && <div className="ind-empty">Loading cameras…</div>}
      {!loading && !cameras.length && <div className="ind-empty">No cameras configured.</div>}

      <div className="ind-grid">
        {cameras.map((c, i) => {
          const up = isOnline(c);
          const idx = c.idx ?? i;
          const name = (c.name || `Cam ${i + 1}`);
          const zone = c.zone ? ` · ${c.zone}` : '';
          return (
            <div key={idx} className={`ind-cell${up ? '' : ' off'}`}
              style={{ cursor: up ? 'zoom-in' : 'default' }}
              onClick={() => up && setMaxCam({ idx, name: c.name })}
              title={up ? 'Click to maximize' : ''}>
              {up && live ? <LiveTile idx={idx} token={token} /> : <div className="ph">📹</div>}
              {up ? <span className="live" /> : <div className="veil">OFFLINE</div>}
              <span className="lbl">{name.toUpperCase()}{zone}</span>
              {up && (
                <button className="ind-snap-btn" title="Download snapshot"
                  onClick={e => { e.stopPropagation(); downloadSnapshot(idx, c.name, token); }}>📷</button>
              )}
            </div>
          );
        })}
      </div>

      {maxCam && <MaxView cam={maxCam} token={token} onClose={() => setMaxCam(null)} />}
    </IndustrialLayout>
  );
}
